(() => {
  if (window.vorugGuideMotion || !Element.prototype.animate || !window.IntersectionObserver) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const active = new Set();
  const jobs = new WeakMap();
  const prepared = new WeakSet();
  const cleanups = new Map();
  const animate = (element, frames, duration = 700, delay = 0) => {
    if (!element || reduced.matches) return;
    const animation = element.animate(frames, { duration, delay, easing: ease, fill: 'backwards' });
    active.add(animation);
    animation.finished.then(() => { active.delete(animation); animation.cancel(); }, () => active.delete(animation));
    return animation;
  };
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    observer.unobserve(entry.target);
    const job = jobs.get(entry.target);
    jobs.delete(entry.target);
    if (!reduced.matches) job?.();
  }), { rootMargin: '0px 0px -4% 0px', threshold: .05 });
  const enter = (element, job) => {
    if (!element || prepared.has(element) || reduced.matches) return;
    prepared.add(element);
    if (element.getBoundingClientRect().bottom < 0) return;
    jobs.set(element, job);
    observer.observe(element);
  };
  const headingEntrance = heading => {
    if (!heading || heading.dataset.guideWords) return;
    // Preserve the heading's accessible text; the visual mask wraps words only.
    const text = heading.textContent.trim();
    heading.dataset.guideWords = 'true';
    heading.setAttribute('aria-label', text);
    const fragment = document.createDocumentFragment();
    const inners = [];
    text.split(/(\s+)/).forEach(part => {
      if (/^\s+$/.test(part)) { fragment.append(document.createTextNode(part)); return; }
      if (!part) return;
      const outer = document.createElement('span');
      outer.className = 'vguide-word'; outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.className = 'vguide-word-inner'; inner.textContent = part;
      outer.append(inner); fragment.append(outer); inners.push(inner);
    });
    heading.replaceChildren(fragment);
    const trigger = heading.closest('.vsg-photo-banner') || heading;
    enter(trigger, () => {
      inners.forEach((word, i) => animate(word,
        [{ transform: 'translateY(110%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], 700, 80 + Math.min(i * 35, 140)));
    });
  };
  const photoEntrance = (frame, image, closing = false) => {
    if (!frame || !image) return;
    enter(frame, () => {
      const reveal = () => {
        const bounds = frame.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= innerHeight || reduced.matches || !frame.isConnected) return;
        // Animate the photograph only; headings and links stay independent.
        const mobile = matchMedia('(max-width: 719px)').matches;
        animate(image, [
          { clipPath: 'inset(0 0 8% 0)', opacity: .88, transform: `scale(${mobile ? 1.015 : 1.025})` },
          { clipPath: 'inset(0 0 0 0)', opacity: 1, transform: 'scale(1.006)', offset: .72 },
          { clipPath: 'inset(0 0 0 0)', opacity: 1, transform: 'scale(1)' }
        ], closing ? 950 : 1050);
      };
      if (image.complete && image.naturalWidth) reveal();
      else image.addEventListener('load', reveal, { once: true });
    });
  };
  const setup = () => {
    const guide = document.querySelector('[data-vorug-size-guide]');
    if (!guide) return;
    guide.querySelectorAll('.vsg-hero h1, .vsg-room__heading h2').forEach(headingEntrance);
    guide.querySelectorAll('.vsg-photo-banner figure').forEach(frame => photoEntrance(frame, frame.querySelector('img')));
    guide.querySelectorAll('.vsg-card-grid').forEach(grid => {
      [...grid.children].forEach((card, i) => enter(card, () => animate(card,
        [{ opacity: .75, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], 550, (i % 2) * 55)));
    });
    guide.querySelectorAll('.vsg-checklist li').forEach((row, i) => enter(row, () => animate(row,
      [{ opacity: .75, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], 550, Math.min(i * 55, 110))));
    const main = guide.closest('main');
    const care = main?.querySelector('.section-collapsible-tabs');
    if (care) {
      care.classList.add('vguide-care');
      headingEntrance(care.querySelector('.collapsible-tabs__heading'));
      care.querySelectorAll('.accordion-toggle').forEach((row, i) => {
        if (cleanups.has(row)) return;
        const summary = row.querySelector('.accordion__summary');
        const panel = row.querySelector('.accordion__panel');
        if (!summary || !panel) return;
        summary.setAttribute('role', 'button');
        const sync = () => summary.setAttribute('aria-expanded', String(row.classList.contains('is-open')));
        sync();
        let wasOpen = row.classList.contains('is-open');
        const changes = new MutationObserver(() => {
          const isOpen = row.classList.contains('is-open');
          if (isOpen === wasOpen) return;
          wasOpen = isOpen;
          sync();
          const content = row.querySelector('.accordion__content');
          active.forEach(animation => { if (animation.effect?.target === content) animation.cancel(); });
          if (row.classList.contains('is-open')) animate(row.querySelector('.accordion__content'),
            [{ opacity: .7, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }], 250);
        });
        changes.observe(row, { attributes: true, attributeFilter: ['class'] });
        const space = event => {
          if (event.target === summary && event.code === 'Space') { event.preventDefault(); summary.click(); }
        };
        summary.addEventListener('keydown', space);
        cleanups.set(row, () => { changes.disconnect(); summary.removeEventListener('keydown', space); });
      });
    }
    main?.querySelectorAll('.vorug-help-mosaic--guide-photos .vorug-help-mosaic__tile').forEach(tile => {
      headingEntrance(tile.querySelector('h2'));
      const image = tile.querySelector('img');
      if (image) photoEntrance(tile, image, true);
    });
  };
  // Shopping/navigation controls are never animated or held behind an entrance.
  document.addEventListener('focusin', event => active.forEach(animation => {
    if (animation.effect?.target?.contains(event.target)) animation.cancel();
  }));
  reduced.addEventListener('change', () => { if (reduced.matches) active.forEach(animation => animation.cancel()); else setup(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) active.forEach(animation => animation.finish()); });
  document.addEventListener('shopify:section:load', setup);
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(animation => { if (event.target.contains(animation.effect?.target)) animation.cancel(); });
    event.target.querySelectorAll('*').forEach(element => { observer.unobserve(element); prepared.delete(element); });
    cleanups.forEach((cleanup, row) => { if (event.target.contains(row)) { cleanup(); cleanups.delete(row); } });
  });
  window.vorugGuideMotion = { setup };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true }); else setup();
})();
